/**
 * Driver qualification badges — one definition for every surface that renders them.
 *
 * The backend hands these down as a single comma-joined `endorsements` string
 * ("Hazmat, Tank, 93K, Sleeper, TWIC"), built identically by three endpoints in
 * drivers.py. Three pages render it: the Working/Not Working tab, Planning, and
 * Planning Week.
 *
 * Before this module the label list, the abbreviations, the colour mapping and the
 * filter options were copied across those pages, and the copies had drifted —
 * `Apportioned` rendered orange on two pages and blue on the third (its inline
 * colour chain simply had no branch for it and fell through to the initialiser),
 * and one chain had lost `< 15K` as well. Adding TWIC meant editing eight separate
 * literals. Anything qualification-shaped belongs here now.
 */
import type { TagColor } from '../components/TagBadge';

/**
 * Every qualification the cached-qualification endpoints can emit, in the order
 * they should render. Order matters: it is the badge order in the Quals column and
 * the chip order in the Working tab's stats row.
 *
 * Keep in step with the `endorsements_list` / `considerations_list` builders in
 * backend/app/api/api_v1/endpoints/drivers.py — these strings are matched exactly.
 */
export const DRIVER_QUALIFICATIONS = [
  'Hazmat',
  'Tank',
  '93K',
  'Daycab',
  'Sleeper',
  '< 15K',
  'Apportioned',
  'TWIC',
] as const;

export type DriverQualification = (typeof DRIVER_QUALIFICATIONS)[number];

/**
 * Planning also shows PPE. It rides the same `endorsements` string but is a driver
 * SETTING rather than a cached qualification, so only that endpoint appends it —
 * which is why it is not in DRIVER_QUALIFICATIONS above.
 */
export const PLANNING_QUALIFICATIONS = [...DRIVER_QUALIFICATIONS, 'PPE'] as const;

/** Short forms for the Working tab, whose Quals column is a fixed 130px. */
export const QUALIFICATION_ABBR: Record<string, string> = {
  'Hazmat': 'H',
  'Tank': 'T',
  '93K': '93',
  'Daycab': 'D',
  'Sleeper': 'S',
  '< 15K': '-15',
  'Apportioned': 'A',
  'TWIC': 'Tw',
};

export const qualificationAbbr = (qual: string): string =>
  QUALIFICATION_ABBR[qual] ?? qual;

/** TagBadge colour per qualification. Every colour TagBadge offers is spoken for
 *  except indigo, so a new qualification needs either indigo or a shared colour. */
const QUALIFICATION_COLOR: Record<string, TagColor> = {
  'Hazmat': 'red',
  'Tank': 'purple',
  '93K': 'blue',
  'Daycab': 'yellow',
  'Sleeper': 'gray',
  '< 15K': 'green',
  'Apportioned': 'orange',
  'TWIC': 'pink',
  'PPE': 'green',
};

export const getQualificationColor = (qual: string): TagColor =>
  QUALIFICATION_COLOR[qual] ?? 'gray';

/**
 * Tailwind classes for the Working tab's clickable count chips. Separate from the
 * TagBadge colours above because the chips carry their own border treatment for the
 * active-filter state; the hues deliberately match.
 */
const QUAL_STAT_STYLES: Record<string, { base: string; border: string }> = {
  'Hazmat':      { base: 'bg-red-100    text-red-700    dark:bg-red-900/40    dark:text-red-300',    border: 'border-red-500    dark:border-red-400' },
  'Tank':        { base: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', border: 'border-purple-500 dark:border-purple-400' },
  '93K':         { base: 'bg-blue-100   text-blue-700   dark:bg-blue-900/40   dark:text-blue-300',   border: 'border-blue-500   dark:border-blue-400' },
  'Daycab':      { base: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300', border: 'border-yellow-500 dark:border-yellow-400' },
  'Sleeper':     { base: 'bg-gray-100   text-gray-700   dark:bg-gray-700      dark:text-gray-300',   border: 'border-gray-500   dark:border-gray-400' },
  '< 15K':       { base: 'bg-green-100  text-green-700  dark:bg-green-900/40  dark:text-green-300',  border: 'border-green-500  dark:border-green-400' },
  'Apportioned': { base: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', border: 'border-orange-500 dark:border-orange-400' },
  'TWIC':        { base: 'bg-pink-100   text-pink-700   dark:bg-pink-900/40   dark:text-pink-300',   border: 'border-pink-500   dark:border-pink-400' },
};

const QUAL_STAT_FALLBACK = {
  base: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  border: 'border-gray-500 dark:border-gray-400',
};

/** Falls back rather than throwing: the old inline map was indexed directly, so a
 *  qualification missing an entry destructured `undefined` and crashed the tab. */
export const getQualStatStyle = (qual: string): { base: string; border: string } =>
  QUAL_STAT_STYLES[qual] ?? QUAL_STAT_FALLBACK;

/** MultiSelect options, for the Quals column filters. */
export const qualificationOptions = (
  quals: readonly string[] = DRIVER_QUALIFICATIONS,
): Array<{ value: string; label: string }> =>
  quals.map(q => ({ value: q, label: q }));

/** Split the backend's comma-joined `endorsements` string into its labels. */
export const parseQualifications = (
  endorsements: string | null | undefined,
): string[] => (endorsements ? endorsements.split(', ') : []);

/**
 * Does this driver hold ALL of the selected qualifications?
 *
 * Matches whole labels, not substrings. The Planning tab used to test
 * `endorsements.includes(q)` against the raw joined string, which happens to give
 * the right answer today only because no label is a substring of another — one
 * label like "Tank Endorsed" alongside "Tank" and it would start matching drivers
 * who hold neither.
 */
export const hasAllQualifications = (
  endorsements: string | null | undefined,
  required: readonly string[],
): boolean => {
  if (required.length === 0) return true;
  const held = parseQualifications(endorsements);
  return required.every(q => held.includes(q));
};
